import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Mail, Phone, Globe } from 'lucide-react';
import { useLegal } from '../../components/common/LegalCompany';

const CLAUSES = [
  'The monthly Cable TV subscription is valid for 30 days from the date of activation. Services will remain active only until the validity period expires. Payments made after the expiry date will activate the service from the date of recharge; no previous days will be adjusted.',
  "If you wish to discontinue your Cable TV connection, you must inform our office before the start of the next billing cycle. Otherwise, the next month's subscription charges may become applicable.",
  'If the Cable TV connection is disconnected due to non-payment and later reactivated, a reconnection/reactivation charge of ₹500 will be applicable.',
  'Customers must register complaints only from their registered mobile number. Complaints made from any other number may not be accepted.',
  'Complaint registration hours are 10:00 AM to 7:00 PM. Complaints received after 7:00 PM will be attended to on the next working day.',
  'Our complaint response time is 24 to 48 working hours, depending on the nature of the issue.',
  'Complaints related to the Set Top Box (STB), remote control, accessories, or cable wire may attract service charges. Repair or replacement charges will be applicable wherever necessary.',
  'Customers are responsible for the safety and proper use of the cable wiring and equipment installed inside their premises. Damage caused due to mishandling, alterations, or negligence will be chargeable.',
  'If additional cable wiring or extra connection points are required, the customer shall bear the applicable installation charges.',
  'After disconnection of the Cable TV service, installation charges, activation charges, or service charges will not be refunded.',
  'Once the recharge is successfully completed, it cannot be cancelled, transferred, or refunded.',
  'All customers must comply with the rules and regulations issued by LAXMI CABLE VISION and the applicable Cable TV service providers.',
  'Cable TV channels and channel packages are subject to availability and may change without prior notice as per broadcaster or regulatory guidelines.',
  'If there are any outstanding dues on your account, the Set Top Box may be deactivated until all dues are cleared.',
  'The customer is responsible for keeping their registered contact details updated with LAXMI CABLE VISION.',
  'The Set Top Box (STB) provided under the connection is issued on a "Free-to-Use" basis and remains the property of LAXMI CABLE VISION. If the STB is lost, damaged beyond repair, or not returned at the time of disconnection, the customer will be liable to pay ₹1,500 as replacement charges.',
  'To disconnect your Cable TV connection, you must submit a request along with your registered mobile number, subscriber name, and Set Top Box serial number for verification.',
];

export default function TermsPage() {
  const legal = useLegal();
  const navigate = useNavigate();

  return (
    <div className="terms-page">
      <style>{`
        .terms-page { min-height: 100vh; background: #f8fafc; padding-bottom: 48px; }
        .terms-header {
          background: radial-gradient(circle at 50% 0%, #1e3a8a 0%, #0c1a3d 55%, #060e24 100%);
          padding: 22px 20px calc(28px + env(safe-area-inset-top, 0px));
          padding-top: calc(22px + env(safe-area-inset-top, 0px));
          color: #fff;
        }
        .terms-header-top { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; }
        .terms-back-btn {
          display: flex; align-items: center; justify-content: center; width: 34px; height: 34px;
          border-radius: 10px; border: 1px solid rgba(255,255,255,.3); background: rgba(255,255,255,.08);
          color: #fff; cursor: pointer;
        }
        .terms-title-row { display: flex; align-items: center; gap: 10px; }
        .terms-title { font-size: 1.3rem; font-weight: 800; margin: 0; }
        .terms-subtitle { font-size: .8rem; color: rgba(255,255,255,.65); margin-top: 4px; }
        .terms-body { max-width: 760px; margin: -14px auto 0; padding: 0 16px; }
        .terms-card {
          background: #fff; border-radius: 16px; border: 1px solid #e5e7eb;
          box-shadow: 0 8px 24px rgba(0,0,0,.06); padding: 24px 22px; margin-bottom: 18px;
        }
        .terms-card h3 { font-size: 1.02rem; font-weight: 800; color: #111827; margin: 0 0 10px; }
        .terms-card p { font-size: .88rem; line-height: 1.65; color: #374151; margin: 0 0 8px; }
        .terms-list { list-style: none; margin: 0; padding: 0; counter-reset: clause; }
        .terms-list li {
          counter-increment: clause; font-size: .88rem; line-height: 1.65; color: #374151;
          padding: 12px 0 12px 34px; position: relative; border-bottom: 1px solid #f1f5f9;
        }
        .terms-list li:last-child { border-bottom: none; padding-bottom: 0; }
        .terms-list li:first-child { padding-top: 0; }
        .terms-list li::before {
          content: counter(clause); position: absolute; left: 0; top: 12px;
          width: 22px; height: 22px; border-radius: 50%; background: #eff6ff; color: #1e3a8a;
          font-size: .72rem; font-weight: 800; display: flex; align-items: center; justify-content: center;
        }
        .terms-list li:first-child::before { top: 0; }
        .terms-contact-item { display: flex; align-items: center; gap: 8px; font-size: .85rem; color: #374151; margin-bottom: 8px; }
        .terms-contact-item a { color: var(--primary, #1e3a8a); text-decoration: none; font-weight: 600; }
      `}</style>

      <div className="terms-header">
        <div className="terms-header-top">
          <button type="button" className="terms-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowLeft size={17} />
          </button>
        </div>
        <div className="terms-title-row">
          <FileText size={22} />
          <h1 className="terms-title">Terms &amp; Conditions</h1>
        </div>
        <div className="terms-subtitle">{legal.name} &middot; applies to {legal.portalLabel}</div>
      </div>

      <div className="terms-body">
        <div className="terms-card">
          <p style={{ marginBottom: 0 }}>
            By subscribing to or using any Cable TV service from <strong>{legal.name}</strong>, you agree to the
            following terms and conditions.
          </p>
        </div>

        <div className="terms-card">
          <ul className="terms-list">
            {CLAUSES.map((clause, i) => (
              <li key={i}>{clause.split('LAXMI CABLE VISION').join(legal.name)}</li>
            ))}
          </ul>
        </div>

        <div className="terms-card">
          <h3>Intellectual Property</h3>
          <p>
            All content available on this website, including but not limited to logos, trademarks, images,
            graphics, text, software, designs, icons, and other materials, is the exclusive property of
            {legal.name} or its respective licensors and is protected under applicable copyright,
            trademark, and intellectual property laws.
          </p>
          <p style={{ marginBottom: 0 }}>
            No content from this website may be copied, reproduced, modified, distributed, published,
            transmitted, or used for commercial purposes without prior written permission from
            {legal.name}.
          </p>
        </div>

        <div className="terms-card">
          <h3>Limitation of Liability</h3>
          <p>
            To the maximum extent permitted by applicable law, {legal.name} shall not be liable for
            any direct, indirect, incidental, consequential, special, or punitive damages, including but
            not limited to loss of data, revenue, profits, business opportunities, or service interruptions,
            arising out of or in connection with:
          </p>
          <ul style={{ margin: '0 0 8px', paddingLeft: 20, fontSize: '.88rem', lineHeight: 1.65, color: '#374151' }}>
            <li>The use of or inability to use this website or customer portal.</li>
            <li>Any errors, inaccuracies, or omissions in the content provided.</li>
            <li>Temporary interruptions, technical failures, or maintenance-related downtime.</li>
            <li>Unauthorized access to or alteration of your information.</li>
          </ul>
          <p style={{ marginBottom: 0 }}>Your use of this website is at your own risk.</p>
        </div>

        <div className="terms-card">
          <h3>Governing Law &amp; Jurisdiction</h3>
          <p>
            These Terms &amp; Conditions shall be governed by and construed in accordance with the laws of
            India.
          </p>
          <p style={{ marginBottom: 0 }}>
            Any dispute, claim, or legal proceeding arising out of or relating to these Terms &amp;
            Conditions or your use of our services shall be subject to the exclusive jurisdiction of the
            competent courts located in Ahmedabad, Gujarat, India.
          </p>
        </div>

        <div className="terms-card">
          <h3>Contact Us</h3>
          <p>If you have any questions regarding these Terms &amp; Conditions, please contact us:</p>
          <p style={{ fontWeight: 700, color: '#111827', marginBottom: 10 }}>{legal.name}</p>
          <div className="terms-contact-item">
            <Globe size={14} />
            <a href={legal.portalUrl} target="_blank" rel="noreferrer">{legal.portalLabel}</a>
          </div>
          {legal.email && (
          <div className="terms-contact-item">
            <Mail size={14} />
            <a href={`mailto:${legal.email}`}>{legal.email}</a>
          </div>
          )}
          <div className="terms-contact-item" style={{ marginBottom: 0 }}>
            <Phone size={14} />
            <a href={`tel:${legal.phoneHref}`}>{legal.phone}</a>
          </div>
        </div>

        <div style={{ textAlign: 'center', fontSize: '.78rem', color: '#94a3b8' }}>
          <Link to="/portal/login" style={{ color: 'var(--primary, #1e3a8a)', fontWeight: 600, textDecoration: 'none' }}>
            &larr; Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
